<template>
  <div class="analyses-data-index">
    <Filters
      :filters="analysesStatusFilters"
      filter-key="analysesStatus"
      :initial-data="filtersStatus.analysesStatus"
      :loading="filtersLoading"
    />

    <ViewActionButtons
      :disabled="selectedSamples.length === 0"
      :buttons="topActionButtons"
    />

    <ChangeDilution
      v-if="openChangeDilutionModal && selectedSamples.length > 0"
      :samples="selectedSamples"
      @dialogClose="handleChangeDilutionDialogClose"
    />

    <ChangeSamplingInformation
      v-if="openChangeSamplingInformation && selectedSamples.length > 0"
      :samples="selectedSamples"
      @dialogClose="handleChangeSamplingInformationDialogClose"
    />

    <ProgressDialog
      v-if="displayRawDataProgress"
      :function="updateRawDataFunction"
      :arguments="updateRawDataArguments"
      @dialogClose="rawDataprogressCompleted"
    />

    <TableWrapper
      :table-columns="analysesStatusColumns"
      table-key="analysesStatus"
      configurableColumns
      :enable-selection="true"
      :massive-actions-options="bulkActions"
      :custom-row-class="getRowStatusClass"
      @massiveAction="bulkApply"
      @viewAnalysesDetails="gotoSampleDetails"
      @changeSampleStatus="openUpdateStatus"
      @editLabNotes="openEditLabNotesModal"
      @createLabNotes="openEditLabNotesModal"
      @viewLabNotes="openEditLabNotesModal"
      @changeDilution="changeDilutionForSample"
      @editSamplingInformation="changeSamplingInformation"
      @sampleDraft="setSampleReportDraft"
      @removeSampleDraft="setSampleReportDraftRemove"
      @printSample="printSample"
      @viewReports="openViewReports"
      @createSampleRevision="openCreateRevision"
      @renameSample="openRenameSample"
      @selectionChange="handleMultiSelectionChange"
      @uploadDocument="uploadDocument"
      @removeUploadedReport="removeUploadedReport"
      @showComment="showCommentForSample"
      @downloadAttachment="downloadAttachment"
    />

    <ViewActionButtons :buttons="actionButtons" />

    <UpdateStatus
      v-if="openUpdateStatusModal"
      :samplesId="selectedSamplesId"
      @dialogClose="handleUpdateStatusClose"
    />

    <CreateRevision
      v-if="openCreateRevisionModal"
      :sample="selectedSample"
      :analysisList="analysisList"
      :analysis-type="
        selectedSample.sampleReportType || sampleReportTypeEnum.Default
      "
      @dialogClose="handleCreateRevisionClose"
    />

    <EditSample
      v-if="openRenameSampleModal"
      :initialData="selectedSample"
      @dialogClose="handleRenameSampleClose"
    />

    <DownloadTestReport
      v-if="openViewReport"
      :sample="selectedSample"
      @dialogClose="handleViewReportsClose"
    />

    <ShowComment
      v-if="openShowCommentModal"
      :sample="sample"
      :visible.sync="openShowCommentModal"
      @dialogClose="handleCloseShowCommentForSampleModal"
    />

    <SampleDetailsModal
      v-if="openSampleDetailsModal && detailsSampleIds.length > 0"
      :sampleIds="detailsSampleIds"
      tabbable
      :tabKey="
        detailsSampleIds.length > 1 ? 'smcombined' : `${detailsSampleIds[0]}`
      "
      :tabLabel="$t('analysesStatusManagement.combinedTab')"
      @dialogClose="handleCloseSampleDetailsModal"
    />

    <EditLabNote
      v-if="openEditLabNotes"
      :sample="selectedSample"
      @dialogClose="handleEditLabNotesClose"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import {
    SAMPLE_ACTIONS,
    LAB_SAMPLE_STATUSES,
    analysesStatusManageBulkActions,
    SampleReportType,
    UploadedReportRemovalError,
  } from '@/config/constants';
  import fileSaver from 'file-saver';
  import axios from 'axios';
  import moment from 'moment';
  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import ProgressDialog from '@/components/ProgressDialog.vue';
  import SampleDetailsModal from '@/views/sampleDetails/SampleDetailsModal.vue';
  import { analysesStatusColumns, analysesStatusFilters } from '@/config';
  import { analysesStatusApi, samplesApi } from '@/api';
  import pick from 'lodash.pick';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import acl from '../../services/acl';

  import UpdateStatus from '../../components/UpdateStatus.vue';
  import CreateRevision from './CreateRevision.vue';
  import EditSample from './EditSample.vue';
  import DownloadTestReport from './DownloadTestReports.vue';
  import ChangeDilution from '../analysesData/ChangeDilution.vue';
  import ChangeSamplingInformation from '../analysesData/ChangeSamplingInformation.vue';
  import ShowComment from '../analysesData/ShowComment.vue';
  import EditLabNote from './EditLabNote.vue';

  const REMOVE_REPORT_I18N = 'analysesStatusManagement.removeUploadedReport';
  const REMOVE_REPORT_ERROR_KEYS = {
    [UploadedReportRemovalError.AlreadySigned]: 'alreadySigned',
    [UploadedReportRemovalError.NotRemovable]: 'notRemovable',
  };

  export default {
    name: 'AnalysesStatusManagementIndex',
    mixins: [filterLoadingMixin, abortControllerMixin],
    components: {
      TableWrapper,
      Filters,
      UpdateStatus,
      CreateRevision,
      ViewActionButtons,
      ProgressDialog,
      EditSample,
      DownloadTestReport,
      SampleDetailsModal,
      ChangeDilution,
      ChangeSamplingInformation,
      ShowComment,
      EditLabNote,
    },
    data() {
      return {
        sample: null,
        analysesStatusColumns,
        analysesStatusFilters,
        openShowCommentModal: false,
        openUpdateStatusModal: false,
        openCreateRevisionModal: false,
        openRenameSampleModal: false,
        openViewReport: false,
        openSampleDetailsModal: false,
        openChangeDilutionModal: false,
        openEditLabNotes: false,
        detailsSampleIds: [],
        selectedSample: null,
        selectedSamples: [],
        analysisList: [],
        openChangeSamplingInformation: false,
        actionButtons: [
          {
            text: this.$t('analysesStatusManagement.download'),
            type: 'primary',
            permissions: ['analysesStatusManage'],
            action: this.downloadCSVFile,
          },
        ],
        topActionButtons: [
          {
            text: this.$t('analysesData.bulkActions.viewCombined'),
            type: 'secondary',
            permissions: ['analysesStatusView'],
            action: this.handleOpenSampleDetailsModal,
          },
          {
            text: this.$t(
              'analysesStatusManagement.bulkActions.changeSamplesStatus'
            ),
            type: 'primary',
            permissions: ['analysesStatusManage'],
            action: this.handleOpenUpdateStatusBulk,
          },
        ],
        defaultStatuses: [
          // { key: LAB_SAMPLE_STATUSES.PENDING, label: this.$t('enums.sampleStatuses.pending') },
          {
            key: LAB_SAMPLE_STATUSES.ACCEPTED,
            label: this.$t('enums.sampleStatuses.accepted'),
          },
          {
            key: LAB_SAMPLE_STATUSES.INPROGRESS,
            label: this.$t('enums.sampleStatuses.inprogress'),
          },
          {
            key: LAB_SAMPLE_STATUSES.COMPLETED,
            label: this.$t('enums.sampleStatuses.completed'),
          },
          {
            key: LAB_SAMPLE_STATUSES.CANCELED,
            label: this.$t('enums.sampleStatuses.canceled'),
          },
          // { key: LAB_SAMPLE_STATUSES.REJECTED, label: this.$t('enums.sampleStatuses.rejected') },
          {
            key: LAB_SAMPLE_STATUSES.SUSPENDED,
            label: this.$t('enums.sampleStatuses.suspended'),
          },
          {
            key: LAB_SAMPLE_STATUSES.TOBEVERIFIED,
            label: this.$t('enums.sampleStatuses.toBeVerified'),
          },
          {
            key: LAB_SAMPLE_STATUSES.TOBEAPPROVED,
            label: this.$t('enums.sampleStatuses.toBeApproved'),
          },
        ],
        bulkActions: [
          {
            label: this.$t(
              'analysesStatusManagement.bulkActions.changeSamplesStatus'
            ),
            value: analysesStatusManageBulkActions.changeSamplesStatus,
          },
          {
            label: this.$t(
              'analysesStatusManagement.bulkActions.enableRawData'
            ),
            value: analysesStatusManageBulkActions.enableRawData,
          },
          {
            label: this.$t(
              'analysesStatusManagement.bulkActions.disableRawData'
            ),
            value: analysesStatusManageBulkActions.disableRawData,
          },
        ],
        sampleReportTypeEnum: SampleReportType,
        displayRawData: undefined,
        displayRawDataProgress: false,
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus', 'tablesStatus']),
      selectedSamplesId() {
        return this.selectedSamples.map((sample) => sample.id);
      },
      updateRawDataFunction() {
        return samplesApi.bulkUpdateRawData;
      },
      updateRawDataArguments() {
        return [
          [this.selectedSamplesId, { displayRawData: this.displayRawData }],
        ];
      },
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'updateTableSorting',
        'parseQueryStringStatus',
        'addTab',
      ]),
      async tableRows() {
        // Create abort signal - will cancel any previous pending request
        const signal = this.createAbortController();

        this.filtersLoading = true;
        let aborted = false;
        try {
          let status =
            this.filtersStatus.analysesStatus.status || this.defaultStatuses;

          if (status.length === 0) {
            // restore default statuses
            status = this.defaultStatuses;
          }

          const samplesIndexResponse = await analysesStatusApi.samplesIndex(
            this.tableFilters('analysesStatus'),
            {
              ...this.filtersStatus.analysesStatus,
              status,
            },
            { signal }
          );
          const samplesIndexCountResponse =
            await analysesStatusApi.samplesCount(
              {
                ...this.filtersStatus.analysesStatus,
                status,
              },
              { signal }
            );
          this.updateTableRows({
            tableKey: 'analysesStatus',
            rows: samplesIndexResponse.data,
          });
          this.updateTableCount({
            tableKey: 'analysesStatus',
            count: samplesIndexCountResponse.data.count,
          });
        } catch (error) {
          // Ignore aborted requests
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }

          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          // Only set loading to false if request wasn't aborted
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      // Copy & paste from Table Wrapper.
      checkColumnCondition(column) {
        return (
          !column.columnCondition ||
          (column.columnCondition &&
            column.columnCondition === this.columnCondition)
        );
      },
      // Copy & paste from Table Wrapper & adapted to remove actions
      columns() {
        const { actions, ...result } = Object.keys(
          analysesStatusColumns
        ).reduce((columns, key) => {
          if (
            acl.userCanUse(analysesStatusColumns[key].permissions) &&
            this.checkColumnCondition(analysesStatusColumns[key])
          ) {
            return { ...columns, [key]: analysesStatusColumns[key] };
          }
          return columns;
        }, {});
        return result;
      },
      expandEncodedEnumValue(object, columns) {
        const res = {};
        // Helper per ottenere valore da percorso annidato
        const getNestedValue = (obj, path) => {
          return path.split('.').reduce((current, prop) => {
            return current?.[prop];
          }, obj);
        };

        // Itera su tutte le colonne (non sulle chiavi dell'oggetto)
        Object.values(columns).forEach((columnValue) => {
          // Ottieni il valore usando il percorso completo (supporta nested)
          const value = getNestedValue(object, columnValue.key);
          // Usa il key completo (anche se annidato) come chiave nel risultato
          if (columnValue.type === 'encoded') {
            res[columnValue.key] = this.$t(
              `enums.${columnValue.enum}.${columnValue.decode(value)}`
            );
          } else if (columnValue.type === 'enum') {
            res[columnValue.key] = this.$t(
              `enums.${columnValue.enum}.${value}`
            );
          } else if (columnValue.type === 'date') {
            res[columnValue.key] = value
              ? moment(value).format('DD/MM/YYYY')
              : '';
          } else if (columnValue.type === 'function') {
            res[columnValue.key] = columnValue.get(value, object) ?? '';
          } else {
            // prevent returning null values.
            res[columnValue.key] = value ?? '';
          }
        });
        return res;
      },
      async downloadCSVFile() {
        const { limit, ...filters } = this.tableFilters('analysesStatus');
        const samplesIndexResponse = await analysesStatusApi.samplesIndex(
          filters,
          this.filtersStatus.analysesStatus
        );
        // SMP-511: note cliente SOLO in export
        const comments = {
          key: 'comments',
          label: 'customerComments',
          type: 'text',
        };

        const baseColumns = this.columns();
        const entries = Object.entries(baseColumns);
        const sectorIndex = entries.findIndex(([key]) => key === 'sector');

        const columns = Object.fromEntries([
          ...entries.slice(0, sectorIndex + 1),
          ['comments', comments],
          ...entries.slice(sectorIndex + 1),
        ]);
        const csvHeader = Object.keys(columns).map((item) =>
          this.$t(`filters.${columns[item].label}`)
        );
        const itemsKeys = Object.keys(columns).map((item) => columns[item].key);
        const defaultsObj = itemsKeys.reduce(
          (o, key) => ({ ...o, [key]: '' }),
          {}
        );

        // RFC 4180 compliant
        const sanitizeCSVValue = (value) => {
          if (value === null || value === undefined) {
            return '';
          }
          let stringValue = String(value).trim();
          stringValue = stringValue.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          // Raddoppia le virgolette interne (RFC 4180)
          return `"${stringValue.replace(/"/g, '""')}"`;
        };

        const csvRows = samplesIndexResponse.data.map((line) => {
          const expandedObj = this.expandEncodedEnumValue(line, columns);
          const obj = pick(expandedObj, itemsKeys);
          const mergedObj = { ...defaultsObj, ...obj };

          // Mantieni l'ordine delle colonne come definito in itemsKeys
          return itemsKeys.map((key) => sanitizeCSVValue(mergedObj[key]));
        });

        csvRows.unshift(csvHeader.map(sanitizeCSVValue));

        // Usa \r\n per line ending (Windows standard, funziona ovunque)
        const result = csvRows.map((line) => line.join(';')).join('\r\n');

        // BOM UTF-8 per Excel
        const BOM = '\uFEFF';

        return fileSaver.saveAs(
          new Blob([BOM + result], { type: 'text/csv;charset=utf-8' }),
          'export.csv'
        );
      },
      gotoSampleDetails(row) {
        this.detailsSampleIds = [row.id];
        this.openSampleDetailsModal = true;
        // this.addTab({
        //   name: `sampleDetails${row.id}`,
        //   routeName: 'sampleDetails',
        //   path: '',
        //   query: { sampleIds: [row.id] },
        //   params: { sampleId: row.id },
        //   meta: { label: `${this.$t('sampleDetails.tabLabel')} - ${row.productName}` },
        // });
      },
      // gotoSampleDetailsCombined() {
      //   const sampleIds = this.selectedSamples.map((s) => s.id);
      //   if (sampleIds.length) {
      //     this.addTab({
      //       name: 'sampleDetailssmcombined',
      //       routeName: 'sampleDetails',
      //       path: '',
      //       query: { sampleIds },
      //       params: { sampleId: 'smcombined' },
      //       meta: { label: this.$t('analysesStatusManagement.combinedTab') },
      //     });
      //   }
      // },
      handleOpenSampleDetailsModal() {
        this.detailsSampleIds = this.selectedSamples.map((s) => s.id);
        this.openSampleDetailsModal = true;
      },
      handleCloseSampleDetailsModal() {
        this.detailsSampleIds = [];
        this.openSampleDetailsModal = false;
        this.tableRows();
      },
      handleOpenUpdateStatusBulk() {
        this.openUpdateStatusModal = true;
      },
      openUpdateStatus(row) {
        this.selectedSamples = [row];
        this.openUpdateStatusModal = true;
      },
      handleUpdateStatusClose(status) {
        this.openUpdateStatusModal = false;
        this.selectedSamplesId = null;
        if (status.success) this.tableRows();
      },
      async openCreateRevision(row) {
        this.selectedSample = row;
        // Handle promise nicely with loading inside modal.
        const indexResponse = await samplesApi.samplesAnalysesIndex(row.id);
        this.analysisList = indexResponse.data;
        this.openCreateRevisionModal = true;
      },
      handleCreateRevisionClose(status) {
        this.openCreateRevisionModal = false;
        this.selectedSample = null;
        if (status.success) this.tableRows();
      },
      openRenameSample(row) {
        this.selectedSample = row;
        this.openRenameSampleModal = true;
      },
      handleRenameSampleClose(status) {
        this.openRenameSampleModal = false;
        this.selectedSample = null;
        if (status.success) this.tableRows();
      },
      openViewReports(row) {
        this.selectedSample = row;
        this.openViewReport = true;
      },
      handleViewReportsClose() {
        this.openViewReport = false;
        this.selectedSample = null;
      },
      setSampleReportDraft(row) {
        this.$confirm(
          this.$t('analysesStatusManagement.draft.message'),
          this.$t('analysesStatusManagement.draft.title'),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
            type: 'info',
          }
        )
          .then(async () => {
            await samplesApi.executeSampleAction(row.id, {
              action: SAMPLE_ACTIONS.DRAFT,
            });
            this.$notify({
              showClose: true,
              type: 'success',
              message: this.$t('analysesStatusManagement.draft.success'),
            });
            await this.tableRows();
          })
          .catch((error) => {
            if (typeof error === 'object') {
              this.$notify({
                showClose: true,
                type: 'error',
                message: error.response.data.error.message,
              });
            }
          });
      },
      setSampleReportDraftRemove(row) {
        this.$confirm(
          this.$t('analysesStatusManagement.removeDraft.message'),
          this.$t('analysesStatusManagement.removeDraft.title'),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
            type: 'info',
          }
        )
          .then(async () => {
            await samplesApi.executeSampleAction(row.id, {
              action: SAMPLE_ACTIONS.REMOVEDRAFT,
            });
            this.$notify({
              showClose: true,
              type: 'success',
              message: this.$t('analysesStatusManagement.removeDraft.success'),
            });
            await this.tableRows();
          })
          .catch((error) => {
            if (typeof error === 'object') {
              this.$notify({
                showClose: true,
                type: 'error',
                message: error.response.data.error.message,
              });
            }
          });
      },
      printSample(row) {
        this.$confirm(
          this.$t('analysesStatusManagement.printSample.message'),
          this.$t('analysesStatusManagement.printSample.title'),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
            type: 'info',
          }
        )
          .then(async () => {
            const response = await samplesApi.printSampleReport(row.id);
            fileSaver.saveAs(
              response.data,
              `sample-report-${row.labSampleCode}.pdf`
            );
          })
          .catch((error) => {
            this.$notify({
              showClose: true,
              type: 'error',
              message: error.response.data.error.message,
            });
          });
      },
      bulkApply(action, selectedRows) {
        switch (action) {
          case analysesStatusManageBulkActions.changeSamplesStatus:
            this.selectedSamples = selectedRows;
            this.openUpdateStatusModal = true;
            break;
          case analysesStatusManageBulkActions.enableRawData:
            this.selectedSamples = selectedRows;
            this.changeRawData(true);
            break;
          case analysesStatusManageBulkActions.disableRawData:
            this.selectedSamples = selectedRows;
            this.changeRawData(false);
            break;
          default:
            break;
        }
      },
      getRowStatusClass({ row }) {
        if (row.status && row.status === LAB_SAMPLE_STATUSES.REJECTED) {
          return 'sample-row-rejected';
        }
        if (row.stabilityTimepointToken) {
          return 'sample-row-stability';
        }
        return '';
      },
      handleMultiSelectionChange(rows) {
        this.selectedSamples = rows;
      },
      uploadDocument(row) {
        this.$router.push({
          name: 'uploadDocument',
          params: { labSampleId: row.labSampleId },
        });
      },
      async removeUploadedReport(row) {
        const confirmed = await this.$confirm(
          this.$t(`${REMOVE_REPORT_I18N}.message`),
          this.$t(`${REMOVE_REPORT_I18N}.title`),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
            type: 'warning',
          }
        ).catch(() => false);
        if (!confirmed) return;

        try {
          await samplesApi.removeUploadedReport(row.labSampleId);
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t(`${REMOVE_REPORT_I18N}.success`),
          });
          this.tableRows();
        } catch ({ response }) {
          if (!response) return;
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.uploadedReportRemovalError(response.data.error),
          });
        }
      },
      uploadedReportRemovalError({ code, message }) {
        const key = REMOVE_REPORT_ERROR_KEYS[code];
        return key ? this.$t(`${REMOVE_REPORT_I18N}.${key}`) : message;
      },
      changeSamplingInformation(row) {
        this.selectedSamples = [row];
        this.openChangeSamplingInformation = true;
      },
      handleChangeSamplingInformationDialogClose(status) {
        this.selectedSamples = [];
        this.openChangeSamplingInformation = false;
        if (status.success) this.tableRows();
      },
      changeDilutionForSample(row) {
        this.selectedSamples = [row];
        this.openChangeDilutionModal = true;
      },
      handleChangeDilutionDialogClose(status) {
        this.selectedSamples = [];
        this.openChangeDilutionModal = false;
        if (status.success) this.tableRows();
      },
      showCommentForSample(row) {
        this.openShowCommentModal = true;
        this.sample = row;
      },
      handleCloseShowCommentForSampleModal() {
        this.sample = null;
        this.openShowCommentModal = false;
      },
      openEditLabNotesModal(row) {
        this.selectedSample = row;
        this.openEditLabNotes = true;
      },
      handleEditLabNotesClose(status) {
        this.openEditLabNotes = false;
        this.selectedSample = null;
        if (status.success) this.tableRows();
      },
      changeRawData(displayRawData) {
        this.displayRawData = displayRawData;
        this.displayRawDataProgress = true;
      },
      rawDataprogressCompleted() {
        this.displayRawData = undefined;
        this.displayRawDataProgress = false;
        this.tableRows();
      },
      async downloadAttachment(row) {
        try {
          const blob = await samplesApi.downloadAttachmentsZip(row.id);
          fileSaver.saveAs(blob.data, `sample-${row.id}-attachment.zip`);
          return false;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
          return false;
        }
      },
    },
    created() {
      const query = { ...this.$route.query };
      // if (!query.filterData) {
      //   query.filterData = JSON.stringify(
      //     {
      //       status: this.defaultStatuses,
      //     },
      //   );
      //   query.filterKey = 'analysesStatus';
      // }
      if (!query.sorting) {
        query.sorting = JSON.stringify({
          prop: 'acceptedAt',
          order: 'descending',
        });
        query.tableKey = 'analysesStatus';
      }
      this.parseQueryStringStatus(query);
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>

<style lang="scss">
  .analyses-data-index {
    .table-cell-status {
      position: relative;

      @each $modifier, $map in $sample-statuses {
        &--#{$modifier} {
          & .cell {
            padding-left: 20px;
          }

          &:after {
            content: '';
            display: block;
            width: 10px;
            position: absolute;
            height: 100%;
            top: 0;
            left: 0;
            background: map-get($map, 'background');
          }
        }
      }
    }
    .el-table {
      &__row {
        &.sample-row-rejected {
          background: map-get(
            map-get($sample-statuses, 'rejected'),
            'background'
          );
        }
        &.sample-row-stability {
          background: #1b3a4b;
        }
      }
    }
  }
</style>
